import { h, FunctionComponent } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import classnames from 'classnames';

import { IconClose } from '../icons';
import { Button } from '../button';

export interface IModalProps {
    className?: string;
    closeButton?: boolean;
    large?: boolean;
    onHide?: Function;
}

export const Modal: FunctionComponent<IModalProps> = ({
    children,
    className,
    closeButton = true,
    large = false,
    onHide = () => {},
    ...rest
}) => {
    const ref = useRef<HTMLDialogElement | null>(null);

    useEffect(() => {
        if (ref.current && ref.current.showModal) {
            ref.current.showModal();
        }
    }, []);

    const onClose = () => {
        if (ref.current && ref.current.close) {
            ref.current.close();
        }
        onHide();
    };

    useEffect(() => {
        const handleOverlayClick = (e: MouseEvent) => {
            if (!ref.current) {
                return;
            }

            const r = ref.current.getBoundingClientRect();
            const isOutsideClick =
                e.clientX < r.left ||
                e.clientX > r.right ||
                e.clientY < r.top ||
                e.clientY > r.bottom;

            if (isOutsideClick && (e.currentTarget as HTMLElement).tagName === 'DIALOG') {
                onClose();
            }
        };
        ref.current && ref.current.addEventListener('click', handleOverlayClick, true);
        return () => {
            ref.current && ref.current.removeEventListener('click', handleOverlayClick, true);
        };
    }, []);

    return (
        <dialog
            ref={ref}
            className={classnames(
                'c-modal',
                {
                    'c-modal--large': large,
                },
                className,
            )}
            onKeyDown={(event) => event.code === 'Escape' && onClose()}
            {...rest}
        >
            {closeButton && (
                <Button icon className="c-modal__close" onClick={() => onClose()}>
                    <IconClose title="Close modal" />
                </Button>
            )}

            <div className="c-modal__inner">{children}</div>
        </dialog>
    );
};
